/**
 * Quy tắc kho ↔ loại hàng (nguồn duy nhất cho máy chủ và giao diện).
 * Mỗi kho thuộc một nhóm nghiệp vụ theo `warehouses.type`; mỗi nhóm chỉ nhận đúng loại hàng của nó
 * (`inventory_*.item_type`), tránh gắn vật tư vào kho thành phẩm, thành phẩm vào kho kỹ thuật…
 * Kho chi nhánh (BRANCH) nhận mọi loại hàng.
 */
export type WarehouseDomain = 'MATERIAL' | 'FG' | 'TECH' | 'WIP' | 'SCRAP';

export interface WarehouseDomainDef {
  domain: WarehouseDomain;
  /** Đoạn đường dẫn: /warehouse/{slug} */
  slug: string;
  label: string;
  /** Giá trị `warehouses.type` thuộc nhóm */
  warehouseTypes: string[];
  /** Giá trị `item_type` được phép trong kho (gồm mã cũ cùng nhóm) */
  itemTypes: string[];
  /** Mã loại hàng ghi cho dòng mới */
  defaultItemType: string;
  /** Tên loại hàng cho thông báo */
  itemLabel: string;
}

export const WAREHOUSE_DOMAINS: Record<WarehouseDomain, WarehouseDomainDef> = {
  MATERIAL: {
    domain: 'MATERIAL', slug: 'material', label: 'Kho Vật tư',
    warehouseTypes: ['MATERIAL', 'MAIN'], itemTypes: ['MATERIAL'], defaultItemType: 'MATERIAL', itemLabel: 'vật tư',
  },
  FG: {
    domain: 'FG', slug: 'fg', label: 'Kho Thành phẩm',
    warehouseTypes: ['FG'], itemTypes: ['PRODUCT', 'FG'], defaultItemType: 'PRODUCT', itemLabel: 'thành phẩm',
  },
  TECH: {
    domain: 'TECH', slug: 'tech', label: 'Kho Kỹ thuật',
    warehouseTypes: ['TECH', 'TOOLING'], itemTypes: ['TOOLING', 'TECH'], defaultItemType: 'TOOLING', itemLabel: 'khuôn / bản kẽm / CCDC',
  },
  WIP: {
    domain: 'WIP', slug: 'wip', label: 'Kho Bán thành phẩm',
    warehouseTypes: ['WIP'], itemTypes: ['WIP'], defaultItemType: 'WIP', itemLabel: 'bán thành phẩm',
  },
  SCRAP: {
    domain: 'SCRAP', slug: 'scrap', label: 'Kho Phế liệu',
    // Phế liệu thu hồi từ vật tư, thành phẩm hoặc bán thành phẩm hỏng
    warehouseTypes: ['SCRAP'], itemTypes: ['MATERIAL', 'PRODUCT', 'FG', 'WIP'], defaultItemType: 'MATERIAL', itemLabel: 'phế liệu',
  },
};

export const WAREHOUSE_DOMAIN_LIST: WarehouseDomainDef[] = Object.values(WAREHOUSE_DOMAINS);

const ITEM_TYPE_LABELS: Record<string, string> = {
  MATERIAL: 'vật tư', PRODUCT: 'thành phẩm', FG: 'thành phẩm', TOOLING: 'khuôn / bản kẽm / CCDC', TECH: 'khuôn / bản kẽm / CCDC', WIP: 'bán thành phẩm',
};

export function itemTypeLabel(itemType?: string | null): string {
  return ITEM_TYPE_LABELS[String(itemType || '').toUpperCase()] || String(itemType || 'hàng');
}

/** Nhóm nghiệp vụ của một loại kho; null = kho nhận mọi loại hàng (chi nhánh / không rõ) */
export function warehouseDomainOf(warehouseType?: string | null): WarehouseDomain | null {
  const t = String(warehouseType || '').trim().toUpperCase();
  return WAREHOUSE_DOMAIN_LIST.find((d) => d.warehouseTypes.includes(t))?.domain ?? null;
}

/**
 * Nhóm nghiệp vụ từ tham số trên đường dẫn / bộ lọc: chấp nhận slug ('tech'), mã nhóm ('TECH'),
 * mã loại kho ('TOOLING') hoặc mã loại hàng ('PRODUCT'). Không nhận ra → null.
 */
export function resolveWarehouseDomain(value?: string | null): WarehouseDomain | null {
  const v = String(value || '').trim();
  if (!v || v.toUpperCase() === 'ALL') return null;
  const upper = v.toUpperCase();
  const bySlug = WAREHOUSE_DOMAIN_LIST.find((d) => d.slug === v.toLowerCase());
  if (bySlug) return bySlug.domain;
  if (upper in WAREHOUSE_DOMAINS) return upper as WarehouseDomain;
  if (upper === 'PRODUCT') return 'FG';
  return warehouseDomainOf(upper);
}

/** Loại hàng được phép trong kho; null = không giới hạn */
export function allowedItemTypesForWarehouse(warehouseType?: string | null): string[] | null {
  const domain = warehouseDomainOf(warehouseType);
  return domain ? WAREHOUSE_DOMAINS[domain].itemTypes : null;
}

export function isItemTypeAllowedInWarehouse(warehouseType: string | null | undefined, itemType: string | null | undefined): boolean {
  const allowed = allowedItemTypesForWarehouse(warehouseType);
  if (!allowed) return true;
  return allowed.includes(String(itemType || '').toUpperCase());
}

export interface WarehouseLineMismatch {
  lineIndex: number;
  itemCode?: string | null;
  itemName?: string | null;
  itemType: string;
  warehouseCode?: string | null;
  warehouseName?: string | null;
  warehouseType: string;
}

/**
 * Các dòng phiếu gắn sai loại hàng cho kho nguồn / kho đích.
 * Kho null (phiếu nhập không có kho nguồn, phiếu xuất không có kho đích) bỏ qua.
 */
export function findWarehouseLineMismatches(
  lines: Array<{ itemType?: string | null; itemCode?: string | null; itemName?: string | null }>,
  warehouses: Array<{ code?: string | null; name?: string | null; type?: string | null } | null | undefined>,
): WarehouseLineMismatch[] {
  const out: WarehouseLineMismatch[] = [];
  lines.forEach((line, lineIndex) => {
    for (const wh of warehouses) {
      if (!wh) continue;
      if (!isItemTypeAllowedInWarehouse(wh.type, line.itemType)) {
        out.push({
          lineIndex, itemCode: line.itemCode, itemName: line.itemName, itemType: String(line.itemType || ''),
          warehouseCode: wh.code, warehouseName: wh.name, warehouseType: String(wh.type || ''),
        });
        break;
      }
    }
  });
  return out;
}

/**
 * Kiểm tra dòng phiếu với kho nguồn / kho đích đọc từ DB.
 * Trả về thông báo lỗi cho người dùng, hoặc null khi hợp lệ. Bên gọi tự ném lỗi theo kiểu của mình.
 * @param db Prisma client hoặc transaction client
 */
export async function checkWarehouseLines(
  db: any,
  warehouseIds: Array<string | null | undefined>,
  lines: Array<{ itemType?: string | null; itemCode?: string | null; itemName?: string | null }>,
): Promise<string | null> {
  const ids = Array.from(new Set(warehouseIds.filter((id): id is string => !!id)));
  if (ids.length === 0 || !lines?.length) return null;
  const rows = await db.warehouse.findMany({ where: { id: { in: ids } }, select: { id: true, code: true, name: true, type: true } });
  const ordered = ids.map((id) => rows.find((w: any) => w.id === id) || null);
  const mismatches = findWarehouseLineMismatches(lines, ordered);
  return mismatches.length ? formatWarehouseMismatchMessage(mismatches) : null;
}

/** Thông báo cho người dùng khi phiếu có dòng sai loại kho (tối đa 3 dòng đầu) */
export function formatWarehouseMismatchMessage(mismatches: WarehouseLineMismatch[]): string {
  const head = mismatches.slice(0, 3).map((m) => {
    const domain = warehouseDomainOf(m.warehouseType);
    const accepts = domain ? WAREHOUSE_DOMAINS[domain].itemLabel : 'mọi loại hàng';
    const item = m.itemCode || m.itemName || `dòng ${m.lineIndex + 1}`;
    return `${item} là ${itemTypeLabel(m.itemType)}, nhưng ${m.warehouseName || m.warehouseCode || 'kho đã chọn'} chỉ nhận ${accepts}`;
  });
  const more = mismatches.length > 3 ? ` (và ${mismatches.length - 3} dòng khác)` : '';
  return `Phiếu có hàng không đúng loại kho: ${head.join('; ')}${more}. Vui lòng chọn đúng kho hoặc đúng mặt hàng.`;
}

/**
 * Kho độc lập, không chuyển kho: một mặt hàng chỉ lưu ở một kho (nhập kho nào xuất kho đó).
 * Trả thông báo khi có mặt hàng trên phiếu đang còn tồn ở kho khác với kho của phiếu.
 */
export async function checkItemsSingleWarehouse(
  db: any,
  warehouseId: string | null | undefined,
  lines: Array<{ itemId?: string | null; itemCode?: string | null; itemName?: string | null }>,
): Promise<string | null> {
  const itemIds = Array.from(new Set((lines || []).map((l) => l.itemId).filter((id): id is string => !!id)));
  if (!warehouseId || itemIds.length === 0) return null;
  const elsewhere = await db.inventoryBalance.findMany({
    where: {
      itemId: { in: itemIds },
      warehouseId: { not: warehouseId },
      OR: [{ qtyOnHand: { gt: 0.0001 } }, { qtyOnHand: { lt: -0.0001 } }],
    },
    select: { itemId: true, warehouseId: true },
  });
  if (!elsewhere.length) return null;
  const whIds = Array.from(new Set(elsewhere.map((b: any) => b.warehouseId)));
  const whs = await db.warehouse.findMany({ where: { id: { in: whIds } }, select: { id: true, code: true, name: true } });
  const whName = (id: string) => {
    const w = whs.find((x: any) => x.id === id);
    return w?.name || w?.code || 'kho khác';
  };
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const b of elsewhere) {
    if (seen.has(b.itemId)) continue;
    seen.add(b.itemId);
    const line = lines.find((l) => l.itemId === b.itemId);
    parts.push(`${line?.itemCode || line?.itemName || 'Mặt hàng'} đang tồn ở ${whName(b.warehouseId)}`);
  }
  const more = parts.length > 3 ? ` (và ${parts.length - 3} mặt hàng khác)` : '';
  return `Mỗi mặt hàng chỉ lưu ở một kho, không chuyển kho: ${parts.slice(0, 3).join('; ')}${more}. Vui lòng nhập / xuất tại đúng kho đang giữ hàng.`;
}

/**
 * Kho có cho phép xuất âm không (xuất khi tồn chưa đủ, số dư xuống dưới 0).
 * Admin bật / tắt theo từng kho; kho không tìm thấy hoặc chưa có cấu hình → coi như cho phép (mặc định bật cho test & golive).
 */
export async function isNegativeStockAllowed(db: any, warehouseId: string | null | undefined): Promise<boolean> {
  if (!warehouseId) return false;
  const wh = await db.warehouse.findUnique({ where: { id: warehouseId }, select: { allowNegativeStock: true } }).catch(() => null);
  return wh?.allowNegativeStock !== false;
}
